import { CharacterAvatar } from "@/components/character/CharacterAvatar";
import { Nav } from "@/components/Nav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/auth/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@repo/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { format, isToday, isYesterday } from "date-fns";
import { fr } from "date-fns/locale";
import { Loader2, MessageCirclePlus } from "lucide-react";
import { Link } from "react-router-dom";

export type ConversationLastMessage =
  Database["public"]["Views"]["conversations_last_message"]["Row"];

// Helper to format the conversation date
const formatConversationDate = (dateString?: string | null) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isToday(date)) {
    return format(date, "HH:mm", { locale: fr });
  }
  if (isYesterday(date)) {
    return "hier";
  }
  return format(date, "dd/MM/yyyy", { locale: fr });
};

interface ConversationListItemProps {
  conversation: ConversationLastMessage;
}

export const ConversationListItem = ({
  conversation,
}: ConversationListItemProps) => {
  const unreadCount = conversation.unread_count ?? 0;
  const isUnread = unreadCount > 0;

  return (
    <Link
      to={`/chat/${conversation.character_id}`}
      key={conversation.character_id}
      className="group flex items-center px-4 py-4 transition hover:bg-gray-50"
    >
      <div className="mr-4 flex-shrink-0">
        <CharacterAvatar
          name={conversation.character_name ?? ""}
          avatar_url={conversation.character_avatar_url ?? undefined}
          className="h-12 w-12"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <span className="truncate text-base font-semibold text-gray-900">
            {conversation.character_name}
          </span>
          <span
            className={`ml-2 whitespace-nowrap text-sm text-gray-400 ${isUnread ? "text-sylvain-purple" : ""}`}
          >
            {formatConversationDate(conversation.created_at)}
          </span>
        </div>
        <div className="relative mt-1">
          <div className="max-w-xs truncate pr-10 text-sm text-gray-600">
            {conversation.message}
          </div>
          {isUnread && (
            <Badge
              className="bg-sylvain-purple absolute bottom-0 right-0 mb-0.5 mr-0.5 min-w-6 px-2 py-0.5 text-xs font-bold text-white shadow-md"
              aria-label={`${conversation.unread_count} messages non lus`}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </Link>
  );
};

const Home = () => {
  const { user } = useAuth();
  const {
    isDeviceOnboarded,
    disable: disablePushSubscription,
    enable: enablePushSubscription,
    isUpdating: isPushUpdating,
    canEnable: canEnablePush,
  } = useNotifications();

  // Fetch conversations: use the conversations_last_message view
  const { data: conversations, isLoading: isConversationsLoading } = useQuery<
    ConversationLastMessage[]
  >({
    queryKey: ["conversations", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("conversations_last_message")
        .select("*")
        .eq("profile_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ConversationLastMessage[];
    },
    enabled: !!user?.id,
  });

  return (
    <div className="bg-linear-to-br min-h-full from-blue-50 via-purple-50 to-pink-50">
      {/* Notification Onboarding Modal */}
      <Dialog
        open={!isDeviceOnboarded && !!canEnablePush}
        onOpenChange={() => disablePushSubscription()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activez les notifications</DialogTitle>
            <DialogDescription className="pt-2 text-left">
              <div className="mb-2">
                Recevez une alerte dès qu'un personnage vous répond.
              </div>
              <div className="mb-2">
                Vous pourrez toujours désactiver les notifications plus tard
                dans les paramètres.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => disablePushSubscription()}
              variant="ghost"
              className="w-full"
              disabled={isPushUpdating}
            >
              Plus tard
            </Button>
            <Button
              onClick={() => {
                enablePushSubscription();
              }}
              variant="default"
              className="w-full"
              disabled={isPushUpdating}
            >
              Activer les notifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Navigation */}
      <Nav />
      <main className="mx-auto max-w-2xl p-4 sm:p-6 lg:p-8">
        {/* Conversation List */}
        {isConversationsLoading && (
          <div className="animate-fade-in flex flex-col items-center justify-center py-24 text-center text-gray-400">
            <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-indigo-600" />
          </div>
        )}
        {conversations && conversations.length > 0 ?
          <div className="mx-auto max-w-2xl divide-y divide-gray-200 overflow-hidden rounded-xl bg-white/80 shadow-lg">
            {conversations.map((conv) => (
              <ConversationListItem
                conversation={conv}
                key={conv.character_id}
              />
            ))}
          </div>
        : <div className="py-16 text-center">
            <Card className="bg-linear-to-br mx-auto max-w-2xl border-0 from-white to-blue-50 shadow-xl">
              <CardContent className="p-12">
                <h3 className="mb-4 text-2xl font-bold text-gray-900">
                  Prêt pour votre première conversation ?
                </h3>
                <p className="mb-6 text-lg text-gray-600">
                  Choisissez un personnage qui vous inspire et découvrez son
                  univers unique.
                </p>
                <p className="mb-8 text-sm text-gray-500">
                  💾 Toutes vos conversations sont sauvegardées automatiquement
                </p>
                <Link to="/new-conversation">
                  <Button size="lg" variant="default">
                    Voir les personnages
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        }
      </main>
      {/* Floating Action Button */}
      <Link
        to="/new-conversation"
        className="fixed bottom-6 right-6 z-50"
        aria-label="Nouvelle conversation"
      >
        <Button
          size="icon"
          className="bg-linear-to-r flex h-16 w-16 items-center justify-center rounded-full from-indigo-600 to-purple-600 text-white shadow-lg shadow-purple-300 hover:from-indigo-600 hover:to-purple-600"
        >
          <MessageCirclePlus className="h-8 w-8" />
        </Button>
      </Link>
    </div>
  );
};

export default Home;
